/** PII allowlist for transport (TF-AUD-013 alignment). */

import { toWireEventName } from '@trafilea/tss-lib-funnel-schema-ts';
import { FUNNEL_UI_SAFE_PARAM_KEYS } from '@ecommerce/analytics/browser';
import type { UIEventPayloadContext } from './envelope';

const ALLOWED_PARAM_KEYS = new Set<string>(FUNNEL_UI_SAFE_PARAM_KEYS);
const ALLOWED_CONTEXT_KEYS = new Set<keyof UIEventPayloadContext>([
  'funnelId',
  'stepId',
  'blockId',
  'blockType',
  'trigger',
  'pageType',
  'variantKey',
]);

const REDACTED = '[REDACTED]';

const SENSITIVE_PATTERN =
  /email|phone|name|address|password|token|secret|ssn|credit/i;

export function redactParamsForTransport(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (ALLOWED_PARAM_KEYS.has(key)) {
      out[key] = value;
    } else if (SENSITIVE_PATTERN.test(key)) {
      out[key] = REDACTED;
    }
  }
  return out;
}

export function redactContextForTransport(
  context: UIEventPayloadContext,
): Partial<UIEventPayloadContext> {
  const out: Partial<UIEventPayloadContext> = {};
  for (const key of ALLOWED_CONTEXT_KEYS) {
    if (key in context) {
      out[key] = context[key];
    }
  }
  return out;
}

export interface AnalyticsEventShape {
  metadata: {
    event_id: string;      // durable dedup key — required for idempotent ingest
    event_type: string;
    source: string;
    timestamp: string;
    phase: 'observation' | 'result';
    origin: string;
    surface: 'funnel';
    schema_version: 1;
    store_id?: string;
    correlation_id?: string;
    session_id?: string;
  };
  data: Record<string, unknown>;
}

export function translateEnvelopeToAnalyticsEvent(
  envelope: import('./envelope').TssEventEnvelope,
  storeId: string,
): AnalyticsEventShape {
  const payload = envelope.payload as import('./envelope').UIEventPayload;
  return {
    metadata: {
      event_id: envelope.metadata.eventId,
      // D-G / ADR-0011: the bus carries plain behavioral names; the
      // wire/transport leg is the ONLY place the canonical ui.* namespace is
      // applied (idempotent across plain, current, and legacy-compatible input).
      event_type: toWireEventName(envelope.name),
      source: envelope.metadata.source,
      timestamp: envelope.metadata.occurredAt,
      phase:
        envelope.metadata.phase === 'result' ? 'result' : 'observation',
      origin: envelope.metadata.origin,
      surface: 'funnel',
      schema_version: 1,
      store_id: storeId,
      correlation_id: envelope.metadata.correlationId,
      session_id: envelope.metadata.sessionId,
    },
    data: {
      component: payload?.context?.blockType,
      action: envelope.name,
      params: redactParamsForTransport(payload?.params ?? {}),
      // Never send full URLs or query strings. The BFF repeats this allowlist
      // as a trust-boundary backstop.
      context: redactContextForTransport(payload?.context ?? {}),
    },
  };
}

export interface TransportAdapterOptions {
  apiBaseUrl: string;
  storeId: string;
  enabled?: boolean;
  batchSize?: number;
  debounceMs?: number;
  /** Automatic retries after transient or partial ingest failures. */
  maxRetryAttempts?: number;
}

export function createTransportAdapter(
  bus: import('./funnel-event-bus').FunnelEventBus,
  options: TransportAdapterOptions,
): { unsubscribe: () => void; flush: () => Promise<void> } {
  const batch: AnalyticsEventShape[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let active = true;
  let retryAttempts = 0;
  const debounceMs = options.debounceMs ?? 2000;
  const batchSize = options.batchSize ?? 20;
  const maxRetryAttempts = options.maxRetryAttempts ?? 2;

  function scheduleFlush(delayMs = debounceMs): void {
    if (!active) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, delayMs);
  }

  async function flush(): Promise<void> {
    if (batch.length === 0 || !options.enabled) return;
    const events = batch.splice(0, batch.length);
    try {
      const response = await fetch(`${options.apiBaseUrl}/v1/storefront/telemetry/analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      });

      if (!response.ok) {
        if (response.status === 429 || response.status >= 500) {
          throw new Error(`funnel UI ingest temporarily unavailable (${response.status})`);
        }
        // Validation/authentication failures are not retryable without a code or
        // configuration change. Drop this batch instead of creating a request loop.
        retryAttempts = 0;
        return;
      }

      // The BFF publishes every event independently and can return HTTP 202 with
      // a non-zero rejected count. Replaying the complete batch is safe because
      // every envelope carries a stable event_id for downstream idempotency.
      if (typeof response.json === 'function') {
        const result = (await response.json().catch(() => null)) as {
          rejected?: unknown;
        } | null;
        if (result !== null && typeof result.rejected === 'number' && result.rejected > 0) {
          throw new Error(`funnel UI ingest partially rejected ${result.rejected} event(s)`);
        }
      }
      retryAttempts = 0;
    } catch {
      batch.unshift(...events);
      if (active && retryAttempts < maxRetryAttempts) {
        const retryDelayMs = debounceMs * (2 ** retryAttempts);
        retryAttempts += 1;
        scheduleFlush(retryDelayMs);
      } else {
        // The exhausted budget belongs to this failed delivery sequence, not
        // to future funnel actions. Keep the stable batch queued, but allow a
        // later event or explicit lifecycle flush to start a fresh retry cycle.
        retryAttempts = 0;
      }
    }
  }

  const sub = bus.on('*', (envelope) => {
    if (!options.enabled) return;
    // Intents are executable local commands. Shipping them would let a click
    // masquerade as a completed operation and could also feed a command loop.
    if (envelope.metadata.phase === 'intent') return;
    batch.push(translateEnvelopeToAnalyticsEvent(envelope, options.storeId));
    if (batch.length >= batchSize) {
      void flush();
    } else {
      scheduleFlush();
    }
  });

  // A large share of funnel actions navigate immediately. Start the keepalive
  // request before the page is discarded instead of losing the debounced tail.
  const flushOnPageHide = () => {
    void flush();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushOnPageHide);
  }

  const unsubscribe = () => {
    active = false;
    sub.unsubscribe();
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', flushOnPageHide);
    }
    // React cleanup/HMR may dispose the provider before the debounce expires.
    // The keepalive fetch remains non-blocking for the commerce interaction.
    void flush();
  };

  return { unsubscribe, flush };
}
